import { useCallback, useState } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { AddToPOModal } from '@/components/requests/AddToPOModal'
import { CustomerSidebar } from '@/components/requests/CustomerSidebar'
import { DemandSummaryModal } from '@/components/requests/DemandSummaryModal'
import { LogRequestModal } from '@/components/requests/LogRequestModal'
import { RequestFilters } from '@/components/requests/RequestFilters'
import { RequestTable } from '@/components/requests/RequestTable'
import {
  OPEN_STATUSES,
  useProductRequests,
  useRequestCounts,
  type ProductRequest,
  type ProductRequestFilters,
} from '@/hooks/useProductRequests'

function TableSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  )
}

export default function ProductRequests() {
  // Filters are page-local state rather than uiStore: nothing outside this
  // page reads them, and TopTabs already gets its badge from the counts query.
  const [filters, setFilters] = useState<ProductRequestFilters>({})
  const { data: requests = [], isLoading, isError, refetch } = useProductRequests(filters)
  const { data: counts } = useRequestCounts()

  const totalCount = counts ? Object.values(counts).reduce((s, n) => s + n, 0) : 0
  const openCount = counts
    ? OPEN_STATUSES.reduce((s, k) => s + (counts[k] ?? 0), 0)
    : 0

  const handleFilterChange = useCallback((patch: Partial<ProductRequestFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
  }, [])

  const clearFilters = useCallback(() => setFilters({}), [])

  // One piece of state per surface. `editRequest` doubles as the mode flag for
  // LogRequestModal — null while `logOpen` means create.
  const [logOpen, setLogOpen] = useState(false)
  const [editRequest, setEditRequest] = useState<ProductRequest | null>(null)
  const [addToPORequest, setAddToPORequest] = useState<ProductRequest | null>(null)
  const [customerRequest, setCustomerRequest] = useState<ProductRequest | null>(null)
  const [demandOpen, setDemandOpen] = useState(false)

  const handleLogRequest = useCallback(() => {
    setEditRequest(null)
    setLogOpen(true)
  }, [])
  const handleDemandSummary = useCallback(() => setDemandOpen(true), [])
  const handleEdit = useCallback((req: ProductRequest) => {
    setEditRequest(req)
    setLogOpen(true)
  }, [])
  const handleAddToPO = useCallback((req: ProductRequest) => setAddToPORequest(req), [])
  const handleCustomerClick = useCallback(
    (req: ProductRequest) => setCustomerRequest(req),
    []
  )

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      {/* Page header */}
      <div className="flex items-center gap-3 border-b border-[var(--line-warm)] px-5 py-3.5 bg-[var(--surface-card)]">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
          Product Requests
        </h1>
        <div className="text-xs text-[var(--mute)]">
          {openCount} open · {totalCount} total
        </div>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={handleDemandSummary}
        >
          View Demand Summary
        </Button>
        <Button size="sm" className="h-7 text-xs" onClick={handleLogRequest}>
          + Log Request
        </Button>
      </div>

      <RequestFilters
        filters={filters}
        onChange={handleFilterChange}
        resultCount={requests.length}
      />

      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {isLoading && <TableSkeleton />}

        {isError && !isLoading && (
          <div className="flex justify-center py-12">
            <Card className="w-80 rounded-none shadow-none border-[var(--line-warm)] bg-[var(--surface-card)]">
              <CardContent className="flex flex-col items-center gap-3 pt-6 pb-5">
                <AlertCircle className="h-8 w-8 text-[var(--status-rejected-fg)]" />
                <p className="text-sm text-[var(--ink-2)] text-center">
                  Failed to load product requests
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => refetch()}
                >
                  <RefreshCw className="mr-1.5 h-3 w-3" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {!isLoading && !isError && (
          <RequestTable
            data={requests}
            onEdit={handleEdit}
            onAddToPO={handleAddToPO}
            onCustomerClick={handleCustomerClick}
            onClearFilters={clearFilters}
          />
        )}
      </div>

      <LogRequestModal
        open={logOpen}
        onOpenChange={(o) => {
          setLogOpen(o)
          if (!o) setEditRequest(null)
        }}
        editRequest={editRequest}
      />
      <AddToPOModal
        request={addToPORequest}
        open={addToPORequest !== null}
        onOpenChange={(o) => {
          if (!o) setAddToPORequest(null)
        }}
      />
      <DemandSummaryModal open={demandOpen} onOpenChange={setDemandOpen} />
      <CustomerSidebar
        request={customerRequest}
        onClose={() => setCustomerRequest(null)}
      />
    </div>
  )
}
